import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { TrendingUp, TrendingDown, FileText, Minus } from "lucide-react";
import api from "../lib/api";
import { logError } from "../lib/logger";

function fmtPrice(symbol, v) {
  if (v === null || v === undefined) return "—";
  const s = (symbol || "").toUpperCase();
  let dec = 2;
  if (s.includes("JPY")) dec = 3;
  else if (s.includes("EUR") && s.includes("USD")) dec = 5;
  else if (s.includes("BTC") || s.includes("ETH")) dec = 2;
  return v.toLocaleString("it-IT", { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

function fmtUsd(v) {
  const sign = v >= 0 ? "+" : "-";
  return `${sign}${Math.abs(v).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}$`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" });
  } catch { return "—"; }
}

function tradeState(t) {
  if (t.filled) return "FILLED";
  if (t.order_type === "LIMIT") return "LIMIT";
  return "NOW";
}

function strategyName(label) {
  if (label === "LIQUIDITY POOL") return "Liquidity Pool";
  if (label === "STOCH TREND") return "Stoch Trend";
  return "Manuale";
}

function SummaryStat({ label, value, color }) {
  return (
    <div className="bg-black/30 border border-white/10 rounded-md p-3">
      <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{label}</div>
      <div className={`font-mono text-lg font-semibold mt-0.5 ${color || "text-white"}`}>{value}</div>
    </div>
  );
}

function TradeRow({ t }) {
  const be = t.status === "be_hit";
  const win = t.status === "tp_hit";
  const state = tradeState(t);
  const iconWrap = be ? "bg-sky-400/10 text-sky-300" : win ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400";
  const pnlColor = be ? "text-sky-300" : t.pnl >= 0 ? "text-emerald-400" : "text-red-400";
  const resColor = be ? "text-sky-400/70" : t.pnl >= 0 ? "text-emerald-500/70" : "text-red-500/70";
  const exitLabel = be ? "BE" : win ? "TP" : "SL";
  return (
    <div
      data-testid={`closed-row-${t.seq}`}
      className="flex items-center gap-3 py-3 px-3 border-b border-white/5 hover:bg-white/5 transition-colors"
    >
      <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${iconWrap}`}>
        {be ? <Minus size={15} /> : win ? <TrendingUp size={15} /> : <TrendingDown size={15} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs text-zinc-500">ID {t.seq}</span>
          <span className="font-semibold text-sm text-white">{t.symbol}</span>
          <span className={`text-xs font-medium ${t.direction === "BUY" ? "text-emerald-400" : "text-red-400"}`}>
            {t.direction}
          </span>
          <span className="text-[10px] uppercase tracking-wider text-zinc-500 border border-white/10 rounded px-1.5 py-0.5">
            {state}
          </span>
        </div>
        <div className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
          <span>Entry {fmtPrice(t.symbol, t.entry)}$</span>
          <span className="text-zinc-700">·</span>
          <span>{exitLabel} {fmtPrice(t.symbol, t.exit)}$</span>
          {t.strategy_label && (
            <>
              <span className="text-zinc-700">·</span>
              <span>{strategyName(t.strategy_label)}{t.timeframe ? ` ${t.timeframe}` : ""}</span>
            </>
          )}
          <span className="text-zinc-700">·</span>
          <span>{fmtDate(t.closed_at)}</span>
        </div>
      </div>
      <div className="shrink-0 text-right">
        <div className={`font-mono text-sm font-semibold ${pnlColor}`}>
          {be ? "0,00$" : fmtUsd(t.pnl)}
        </div>
        <div className={`text-[10px] uppercase tracking-wider ${resColor}`}>
          {t.result}
        </div>
      </div>
    </div>
  );
}

export function ClosedSignalsModal({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/signals/closed/history")
      .then((r) => setData(r.data))
      .catch((e) => logError("Errore storico segnali chiusi:", e))
      .finally(() => setLoading(false));
  }, [open]);

  const s = data?.summary;

  const renderBody = () => {
    if (loading) {
      return <div data-testid="closed-loading" className="text-center py-16 text-zinc-500 text-sm">Caricamento report...</div>;
    }
    if (!data || data.items.length === 0) {
      return (
        <div data-testid="closed-empty" className="text-center py-16 text-zinc-500 text-sm">
          Nessun trade chiuso al momento.
        </div>
      );
    }
    return <div>{data.items.map((t) => <TradeRow key={t.seq ?? Math.random()} t={t} />)}</div>;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="closed-signals-modal" className="max-w-2xl bg-[#0d0d0d] border-white/10 text-white max-h-[85vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-white/10">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <FileText size={18} className="text-amber-400" />
            Report Segnali Chiusi
          </DialogTitle>
          <p className="text-xs text-zinc-500 mt-1 text-left">
            Elenco cronologico (per ID) di tutti i trade chiusi con profitto/perdita a size fissa 0,10 lotti.
          </p>
        </DialogHeader>

        <div className="px-6 py-4 border-b border-white/10">
          {s && (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <SummaryStat label="Trade chiusi" value={s.count} />
              <SummaryStat label="Vinti / Persi" value={`${s.wins} / ${s.losses}`} color="text-amber-400" />
              <SummaryStat label="Breakeven" value={s.breakeven ?? 0} color="text-sky-300" />
              <SummaryStat label="Win rate" value={`${s.win_rate}%`} color="text-blue-400" />
              <SummaryStat label="P&L totale" value={fmtUsd(s.total_pnl)} color={s.total_pnl >= 0 ? "text-emerald-400" : "text-red-400"} />
            </div>
          )}
        </div>

        <div className="overflow-y-auto flex-1">
          {renderBody()}
        </div>
      </DialogContent>
    </Dialog>
  );
}
